import { Box, CircularProgress, Typography } from '@mui/material';
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { useAuth } from '@/auth/AuthProvider';

interface AuthGuardProps {
  children: ReactNode;
  allowedRoles?: Array<'ADMIN' | 'VENDEDOR' | 'ASISTENTE'>;
}

export const AuthGuard = ({ children, allowedRoles }: AuthGuardProps) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Box
        display='flex'
        flexDirection='column'
        alignItems='center'
        justifyContent='center'
        minHeight='100vh'
        gap={2}
      >
        <CircularProgress size={48} />
        <Typography variant='body1' color='text.secondary'>
          Verificando sesión...
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to='/login' state={{ from: location }} replace />;
  }

  // Check role-based access
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    // For role-based restrictions, show access denied
    return (
      <Box
        display='flex'
        flexDirection='column'
        alignItems='center'
        justifyContent='center'
        minHeight='100vh'
        gap={2}
        p={3}
      >
        <Typography variant='h5' color='error' gutterBottom>
          Acceso Denegado
        </Typography>
        <Typography variant='body1' color='text.secondary' textAlign='center'>
          No tienes permisos suficientes para acceder a esta página.
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Roles requeridos: {allowedRoles.join(', ')} | Tu rol: {user.role}
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
};